import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { Box, Button, TextField, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { AuthorityMasterType } from "../DataFetch/useAuthorityMaster";
import { useContext } from "react";
import { SessionContext } from "../ManagementRoot";
import { useConfirmDialog } from "../Component/useConfirmDialog";
import { useErrorDialog } from "../Component/useErrorDialog";
import { PostUpdateAuthority } from "../DataFetch/ManagementAPI";

/**
 * 入力フォームの型
 */
interface FormInputType {
    authorityId:number,
    authorityFame:string,
    authorityName:string,
    selectNum:number
}

/**
 * 渡されるPropsの型
 */
interface PropsType {
    /**
     * 編集データ
     */
    editData:AuthorityMasterType | undefined,

    /**
     * 編集完了関数
     */
    CompleteEdit:() => void,

    /**
     * 編集状態設定関数
     * @param editing true or false
     * @returns 
     */
    setEditing:(editing:boolean) => void    
}

/**
 * 権限マスタ編集画面
 * @param param0 画面に渡すProps
 */
export function AuthorityMasterEdit({editData, CompleteEdit, setEditing}:PropsType) {

    /**
     * 新規フラグ
     * 権限マスタは新規登録がないので、常にfalse
     */
    const isNew = editData === undefined ? true : false

    /**
     * セッション確認関数
     */
    const ConfirmSession = useContext(SessionContext);

    const {waitConfirm, ConfirmDialog} = useConfirmDialog();

    const {openErrorDialog, ErrorDialog} = useErrorDialog();

    /**
     * 入力フォームHook
     */
    const {control, handleSubmit } = useForm<FormInputType>({
        defaultValues:{
            authorityId:editData?.authority_id,
            authorityFame:editData?.authority_fame,
            authorityName:editData?.authority_name,
            selectNum:editData?.select_num
        }
    });

    /**
     * 保存処理
     * @param data フォームに入力されたデータ
     * @returns 
     */
    async function onSubmit(data:FormInputType) {
        
        if(!await ConfirmSession()) {
            return;
        }

        await waitConfirm("保存します。よろしいですか？")
        .then(res => {
            res && PostUpdateAuthority({
                authorityId:data.authorityId,
                authorityName:data.authorityName,
                selectNum:data.selectNum,
            })
            .then(res => {
                res ? CompleteEdit() : openErrorDialog();
            })
        })

    }

    return(
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid2 container direction="row" maxWidth="xl" justifyContent="center" alignItems="center" spacing={2}>
                    <Grid2 xs={12} sm="auto">
                        <Typography variant="body1" sx={{textAlign:{xs:"none",sm:"left"}}}>
                            {"権限ID　　"}
                        </Typography>  
                    </Grid2>
                    <Grid2 xs={12} sm={2}>
                        <Controller
                            name="authorityId"
                            control={control}
                            rules={{
                                required:"必須項目です。",
                                maxLength: {
                                    value: 3,
                                    message:"3桁以下にしてください。"
                                },
                                pattern: {
                                    value: /[0-9]/,
                                    message: "数字のみ入力可能です"
                                }
                            }}
                            render={({field,fieldState}) => (
                                <TextField 
                                    fullWidth
                                    {...field} 
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                    disabled={!isNew}
                                />
                            )}
                        />
                    </Grid2>
                </Grid2>
                <Grid2 container direction="row" maxWidth="xl" justifyContent="center" alignItems="center" spacing={2}>
                    <Grid2 xs={12} sm="auto">
                        <Typography variant="body1" sx={{textAlign:{xs:"none",sm:"left"}}}>
                            {"権限英名　"}
                        </Typography>  
                    </Grid2>
                    <Grid2 xs={12} sm={2}>
                        <Controller
                            name="authorityFame"
                            control={control}
                            rules={{
                                required:"必須項目です。",
                                maxLength: {
                                    value: 30,
                                    message:"30文字以下にしてください。"
                                }
                            }}
                            render={({field,fieldState}) => (
                                <TextField 
                                    fullWidth
                                    {...field} 
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                    disabled={!isNew}
                                />
                            )}
                        />
                    </Grid2>
                </Grid2>

                <Grid2 container direction="row" maxWidth="xl" justifyContent="center" alignItems="center" spacing={2}>
                    <Grid2 xs={12} sm="auto">
                        <Typography variant="body1" sx={{textAlign:{xs:"none",sm:"left"}}}>
                            {"権限和名　"}
                        </Typography>  
                    </Grid2>
                    <Grid2 xs={12} sm={2}>
                        <Controller
                            name="authorityName"
                            control={control}
                            rules={{
                                required:"必須項目です。",
                                maxLength: {
                                    value: 30,
                                    message:"30文字以下にしてください。"
                                }
                            }}
                            render={({field,fieldState}) => (
                                <TextField 
                                    fullWidth
                                    {...field} 
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />
                    </Grid2>
                </Grid2>

                <Grid2 container direction="row" maxWidth="xl" justifyContent="center" alignItems="center" spacing={2}>
                <Grid2 xs={12} sm="auto">
                        <Typography variant="body1" sx={{textAlign:{xs:"none",sm:"left"}}}>
                            {"選択可能数"}
                        </Typography>  
                    </Grid2>
                    <Grid2 xs={12} sm={2}>
                        <Controller
                            name="selectNum"
                            control={control}
                            rules={{
                                required:"必須項目です。",
                                maxLength: {
                                    value: 3,
                                    message:"3桁以下にしてください。"
                                },
                                pattern: {
                                    value: /[0-9]/,
                                    message: "数字のみ入力可能です"
                                }
                            }}
                            render={({field,fieldState}) => (
                                <TextField 
                                    fullWidth
                                    {...field} 
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />
                    </Grid2>
                </Grid2>

                <Box m={1}>
                    <Grid2 container direction="row" maxWidth="xl" justifyContent="center" alignItems="center" spacing={2}>
                        <Grid2>
                            <Button variant="outlined" onClick={() => setEditing(false)}>
                                戻る
                            </Button>
                        </Grid2>
                        <Grid2>
                            <Button type="submit" variant="contained">
                                保存
                            </Button>
                        </Grid2>
                        {/* <Grid2 display={isNew ? "none" : ""}>
                            <Button variant="contained" color="error">
                                削除
                            </Button>
                        </Grid2>                      */}
                    </Grid2>
                </Box>  
            </form>

            <ConfirmDialog/>
            <ErrorDialog/>
        </>
    );
}